import { Injectable } from "@angular/core";
import { Subject, Observable } from "rxjs";
import type { FeatureCollection } from "geojson";
import type { EvidenceLayerRequest, EvidenceLayerRequestGroup, EvidenceLayerUpdateRequest } from "../model/evidence-layer.types";
import type { IStyleLegendMetadata, IVectorStyle } from "../../../shared/model/vector-style-definition.interface";

/** Screenshot of the map plus the view state (bbox and zoom) at the moment of capture. */
export interface ScreenshotResult {
  blob: Blob;
  /** Visible extent as [minLon, minLat, maxLon, maxLat]. */
  bbox: number[];
  /** Map zoom level. */
  zoom: number;
}

/**
 * Function registered by the map component to capture the current map view as a PNG Blob
 * together with the bbox and zoom of the view.
 * Called by the AI get_screenshot frontend tool.
 */
export type ScreenshotProvider = () => Promise<ScreenshotResult>;

/** Union of all map interaction events emitted by the chat module. */
export type MapEvent =
  | { type: "move_to"; lon: number; lat: number; zoom?: number }
  | { type: "set_zoom"; zoom: number }
  | {
      type: "add_data";
      url: string;
      layerType: "WMS" | "GeoJSON" | "WMTS" | "XYZ";
      layerName?: string;
    }
  | {
      type: "zoom_to_extent";
      minLon: number;
      minLat: number;
      maxLon: number;
      maxLat: number;
    }
  | {
      /** Add an image (PNG tile or extract result) as an overlay on the map. */
      type: "add_image_overlay";
      /** Object URL from URL.createObjectURL — caller is responsible for revoking it. */
      blobUrl: string;
      /** Bounding box [minLon, minLat, maxLon, maxLat]. */
      bounds: [number, number, number, number];
      layerName?: string;
    }
  | {
      /**
       * Add a prepared GeoJSON FeatureCollection as a user layer.
       * collection must already be normalized (Mercator, uuid features, .properties metadata)
       * as produced by FileReaderService.prepareGeoJson().
       * styleFunctionName applies a named OL style function registered in BaseViewerService.applyStyleFunctions.
       */
      type: "add_geojson_layer";
      collection: any;
      styleFunctionName?: string;
      /** Explicit display name (e.g. the filename minus extension) overriding the collection name. */
      name?: string;
    }
  | {
      /** Add a GeoTIFF, referenced by URL, as a user layer. Georeferencing is read from the file itself. */
      type: "add_geotiff_layer";
      url: string;
      name?: string;
    }
  | {
      /**
       * Add multiple prepared GeoJSON FeatureCollections as a named group layer.
       * Each entry carries the normalized collection plus optional label and initial visibility.
       * When label is omitted the layer name falls back to the collection's filename property.
       * When visible is omitted the child layer defaults to visible.
       */
      type: "add_group_layer";
      name: string;
      layers: { collection: any; label?: string; visible?: boolean; layer_group?: string }[];
    }
  | {
      /**
       * Add a prepared GeoJSON FeatureCollection as an evidence layer (evidence-layer-ui US3/US4),
       * styled by the block's `data.layer` contract type and, when the contract carries a
       * `plan_ref`, grouped under the producing plan's layer group (stable `plan:<plan_ref>`
       * key, upserted map-side). collection must already be normalized
       * (Mercator, uuid features, .properties metadata) as produced by
       * FileReaderService.prepareGeoJson(). name overrides the layer display name (falls back to
       * the collection's filename property when absent).
       */
      type: "add_evidence_layer";
      collection: any;
      name?: string;
      styleFunctionName: string;
      group?: EvidenceLayerRequestGroup;
    }
  | {
      /**
       * Replace the data of an existing user layer in place (map-layer-block-ui S5, the map-layer
       * card's UPDATE action). layer_id is the get_layer_geometry lineage id, matching
       * overlay_layers[].id. collection must already be prepared
       * (FileReaderService.prepareGeoJson output). style/style_metadata are optional — an
       * unstyled update is a normal state that keeps the layer's current styling. When the layer
       * is NOT found the map side no-ops (the card falls back to ADD).
       */
      type: "update_evidence_layer";
      layer_id: string;
      collection: any;
      name?: string;
      style?: IVectorStyle;
      style_metadata?: IStyleLegendMetadata;
    };

/** Result of resolving a layer name to its geometry — carries the real overlay layer id alongside the GeoJSON. */
export interface LayerGeometryResult {
  geojson: FeatureCollection<any>;
  layer_id: string;
}

/**
 * Function registered by the map component to resolve a layer id to its geometry.
 * Returns the FeatureCollection plus the resolved layer's real id, or null if the layer is not found.
 */
export type LayerGeometryProvider = (layerId: string) => LayerGeometryResult | null;

/**
 * Service for bidirectional communication between the AI chat and the map component.
 * This service only emits RxJS events — the map component (using @vision/core-map-api)
 * is responsible for subscribing to events$ and executing the actual map operations.
 *
 * @example
 * // In the map component:
 * this.mapInteractionService.events$.subscribe(event => {
 *   switch (event.type) {
 *     case 'move_to': coreMap.moveTo(event.lon, event.lat, event.zoom); break;
 *     // ...
 *   }
 * });
 */
@Injectable({ providedIn: "root" })
export class MapInteractionService {
  private readonly eventSubject = new Subject<MapEvent>();

  private layerGeometryProvider: LayerGeometryProvider | null = null;

  private screenshotProvider: ScreenshotProvider | null = null;

  /** Observable stream of map interaction events. */
  get events$(): Observable<MapEvent> {
    return this.eventSubject.asObservable();
  }

  /**
   * Registers a function that the map component provides to resolve layer geometries.
   * Call this from the map container component after the map is initialized.
   * @param fn - Function that receives a layer name and returns a MULTIPOINT WKT string or null.
   */
  registerLayerGeometryProvider(fn: LayerGeometryProvider): void {
    this.layerGeometryProvider = fn;
  }

  /**
   * Returns the GeoJSON FeatureCollection and real layer id for a layer, or null if not found.
   * Requires a provider to be registered via registerLayerGeometryProvider.
   * @param layerId - Stable id of the layer, matching overlay_layers[].id.
   */
  getLayerGeometry(layerId: string): LayerGeometryResult | null {
    return this.layerGeometryProvider?.(layerId) ?? null;
  }

  /**
   * Registers a function that captures the current map view as a PNG Blob.
   * Called from ScreenshotComponent after the map is initialized.
   * @param fn - Async function that renders and captures the map canvas.
   */
  registerScreenshotProvider(fn: ScreenshotProvider): void {
    this.screenshotProvider = fn;
  }

  /**
   * Captures the current map view as a PNG Blob plus its bbox and zoom using the registered provider.
   * Rejects if no provider has been registered yet (map not initialized).
   */
  captureScreenshot(): Promise<ScreenshotResult> {
    if (!this.screenshotProvider) {
      return Promise.reject(new Error("captureScreenshot: no provider registered"));
    }
    return this.screenshotProvider();
  }

  /**
   * Emits a raw MapEvent on the stream.
   * @param event - The event to emit.
   */
  emit(event: MapEvent): void {
    this.eventSubject.next(event);
  }

  /**
   * Moves the map view to the specified coordinates.
   * @param lon - Longitude in degrees.
   * @param lat - Latitude in degrees.
   * @param zoom - Optional zoom level.
   */
  moveTo(lon: number, lat: number, zoom?: number): void {
    this.emit({ type: "move_to", lon, lat, zoom });
  }

  /**
   * Sets the map zoom level.
   * @param zoom - Target zoom level.
   */
  setZoom(zoom: number): void {
    this.emit({ type: "set_zoom", zoom });
  }

  /**
   * Requests the map to add a data layer.
   * @param url - Data source URL.
   * @param layerType - Type of the layer (WMS, GeoJSON, WMTS, XYZ).
   * @param layerName - Optional display name for the layer.
   */
  addData(url: string, layerType: "WMS" | "GeoJSON" | "WMTS" | "XYZ", layerName?: string): void {
    this.emit({ type: "add_data", url, layerType, layerName });
  }

  /**
   * Zooms the map to a bounding box extent.
   * @param minLon - Minimum longitude.
   * @param minLat - Minimum latitude.
   * @param maxLon - Maximum longitude.
   * @param maxLat - Maximum latitude.
   */
  zoomToExtent(minLon: number, minLat: number, maxLon: number, maxLat: number): void {
    this.emit({ type: "zoom_to_extent", minLon, minLat, maxLon, maxLat });
  }

  /**
   * Requests the map to add a GeoJSON FeatureCollection as a user layer.
   * The collection must already be prepared by FileReaderService.prepareGeoJson()
   * (Mercator projection, uuid features, .properties metadata attached).
   * @param collection - Prepared FeatureCollection to add as a user layer.
   * @param styleFunctionName - Optional named OL style function registered in BaseViewerService.applyStyleFunctions.
   */
  addGeoJsonLayer(collection: any, styleFunctionName?: string, name?: string): void {
    this.emit({ type: "add_geojson_layer", collection, styleFunctionName, name });
  }

  /**
   * Requests the map to add a GeoTIFF as a user layer, referenced by URL.
   * Georeferencing (extent, projection) is read directly from the file by the map's GeoTIFF layer.
   * @param url - URL to the GeoTIFF file (authenticated backend URL, or a blob: object URL for inline data).
   * @param name - Optional display name for the layer.
   */
  addGeoTiffLayer(url: string, name?: string): void {
    this.emit({ type: "add_geotiff_layer", url, name });
  }

  /**
   * Requests the map to add multiple GeoJSON FeatureCollections as a named group layer.
   * Each entry must include a collection prepared by FileReaderService.prepareGeoJson(),
   * and may optionally override the child layer label and initial visibility.
   * @param name - Display name for the group layer.
   * @param layers - Array of { collection, label?, visible?, layer_group? } entries.
   */
  addGroupLayer(name: string, layers: { collection: any; label?: string; visible?: boolean; layer_group?: string }[]): void {
    this.emit({ type: "add_group_layer", name, layers });
  }

  /**
   * Requests the map to add a prepared GeoJSON FeatureCollection as an evidence layer
   * (evidence-layer-ui US3/US4). The collection must already be prepared by
   * FileReaderService.prepareGeoJson(); the request carries the contract-derived display name
   * (falls back to the collection name when absent), the named OL style function, and — for
   * plan-produced layers — the stable group descriptor the map side upserts.
   * @param collection - Prepared FeatureCollection to add as the evidence layer.
   * @param request - Contract-derived request (name?, styleFunctionName, group?).
   */
  addEvidenceLayer(collection: any, request: EvidenceLayerRequest): void {
    this.emit({
      type: "add_evidence_layer",
      collection,
      name: request.name,
      styleFunctionName: request.styleFunctionName,
      group: request.group,
    });
  }

  /**
   * Requests an in-place UPDATE of an existing user layer (map-layer-block-ui S5): the map side
   * resolves the layer by `request.layer_id` and replaces its features with the prepared
   * collection, applying the optional inline style. When the layer is not found the map side
   * no-ops — the card is responsible for falling back to ADD.
   * @param request - { layer_id, collection, name?, style?, style_metadata? } with the collection
   *   already prepared by FileReaderService.prepareGeoJson().
   */
  updateEvidenceLayer(request: EvidenceLayerUpdateRequest): void {
    this.emit({
      type: "update_evidence_layer",
      layer_id: request.layer_id,
      collection: request.collection,
      name: request.name,
      style: request.style,
      style_metadata: request.style_metadata,
    });
  }
}
